import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/contexts/AuthContext';
import { formatINRFull, getStatusColor, formatStatus } from '@/lib/utils';
import {
  TrendingUp, FolderKanban, IndianRupee, Target, BarChart3, PieChart,
  Users, FileText, ClipboardCheck, AlertTriangle, Briefcase, Calendar, Car, CreditCard
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart as RPie, Pie, Cell } from 'recharts';

const KPICard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-5 hover:-translate-y-[2px] hover:shadow-sm transition-all duration-200" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between gap-2 mb-3">
      <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold min-w-0">{title}</span>
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon size={16} className="text-white" />
      </div>
    </div>
    <p className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const COLORS = ['#1B5486', '#059669', '#D97706', '#DC2626', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// Director Dashboard
function DirectorDashboard({ data }) {
  const { kpis, charts } = data;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard title="Pipeline Value" value={formatINRFull(kpis.pipeline_value)} icon={TrendingUp} color="bg-[#1B5486]" sub={`${kpis.active_leads} active leads`} />
        <KPICard title="Won Orders YTD" value={formatINRFull(kpis.won_orders_ytd)} icon={Target} color="bg-emerald-600" />
        <KPICard title="Win Rate" value={`${kpis.win_rate}%`} icon={BarChart3} color="bg-amber-600" />
        <KPICard title="Total Expenses" value={formatINRFull(kpis.total_expenses)} icon={IndianRupee} color="bg-red-500" sub={`Bank: ${formatINRFull(kpis.bank_expenses)} | Cash: ${formatINRFull(kpis.cash_expenses)}`} />
        <KPICard title="Gross Margin" value={`${kpis.gross_margin}%`} icon={PieChart} color="bg-purple-600" />
        <KPICard title="Outstanding" value={formatINRFull(kpis.outstanding_receivables)} icon={FolderKanban} color="bg-orange-500" sub={`Revenue: ${formatINRFull(kpis.total_revenue)}`} />
      </div>

      {/* POI-04: Invoice Summary Widget */}
      <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="invoice-summary-widget">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Invoice Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Total Invoiced</p>
            <p className="text-lg font-semibold text-slate-900 font-mono">{formatINRFull(kpis.total_invoiced || 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-emerald-600 mb-1 uppercase tracking-wider">Received</p>
            <p className="text-lg font-semibold text-emerald-700 font-mono">{formatINRFull(kpis.total_inv_received || 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-amber-600 mb-1 uppercase tracking-wider">Outstanding</p>
            <p className="text-lg font-semibold text-amber-700 font-mono">{formatINRFull(kpis.inv_outstanding || 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-red-600 mb-1 uppercase tracking-wider">Overdue (&gt;30d)</p>
            <p className="text-lg font-semibold text-red-700 font-mono">{formatINRFull(kpis.inv_overdue || 0)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="sales-funnel-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Sales Funnel</h3>
          <div className="space-y-2">
            {Object.entries(charts.sales_funnel || {}).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(status)} min-w-[100px] text-center`}>{formatStatus(status)}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                  <div className="bg-[#1B5486] h-full rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(count / Math.max(...Object.values(charts.sales_funnel || {1:1})) * 100, 8)}%` }}>
                    <span className="text-[10px] text-white font-semibold">{count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="region-revenue-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Region-wise Revenue</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={charts.region_revenue || []}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/100000).toFixed(0)}L`} />
              <Tooltip formatter={(v) => formatINRFull(v)} />
              <Bar dataKey="value" fill="#1B5486" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="expense-category-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Expense by Category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RPie><Pie data={charts.expense_by_category || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${formatStatus(name)} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#94a3b8' }}>
              {(charts.expense_by_category || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip formatter={(v) => formatINRFull(v)} /></RPie>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="cash-bank-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Cash vs Bank Expenses</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RPie><Pie data={charts.cash_vs_bank || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
              <Cell fill="#1B5486" /><Cell fill="#D97706" />
            </Pie><Tooltip formatter={(v) => formatINRFull(v)} /></RPie>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="top-projects-table">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Top Projects by Profit</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100">
              <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</th>
              <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Contract</th>
              <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Expenses</th>
              <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Profit</th>
              <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Margin</th>
            </tr></thead>
            <tbody>{(charts.top_projects || []).map((p, i) => (
              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2.5 px-3"><span className="font-medium text-slate-900">{p.project_code}</span><span className="block text-xs text-slate-500 truncate max-w-[200px]">{p.name}</span></td>
                <td className="py-2.5 px-3 text-right font-mono text-slate-700">{formatINRFull(p.contract_value)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-slate-700">{formatINRFull(p.total_expenses)}</td>
                <td className={`py-2.5 px-3 text-right font-mono font-medium ${p.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatINRFull(p.profit)}</td>
                <td className={`py-2.5 px-3 text-right font-medium ${p.margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{p.margin_percent}%</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// Accountant Dashboard
function AccountantDashboard({ data }) {
  const { kpis, charts } = data;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <KPICard title="Total Expenses" value={formatINRFull(kpis.total_expenses)} icon={IndianRupee} color="bg-red-500" />
        <KPICard title="Total Revenue" value={formatINRFull(kpis.total_revenue)} icon={Target} color="bg-emerald-600" />
        <KPICard title="Receivables" value={formatINRFull(kpis.outstanding_receivables)} icon={FolderKanban} color="bg-orange-500" />
        <KPICard title="Cash / Bank Split" value={formatINRFull(kpis.bank_expenses)} icon={CreditCard} color="bg-[#1B5486]" sub={`Cash: ${formatINRFull(kpis.cash_expenses)}`} />
        <KPICard title="Overheads" value={formatINRFull(kpis.total_overheads)} icon={Briefcase} color="bg-purple-600" />
        <KPICard title="Pending Invoices" value={kpis.pending_invoices} icon={ClipboardCheck} color="bg-amber-600" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Expense by Category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RPie><Pie data={charts.expense_by_category || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${formatStatus(name)} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#94a3b8' }}>
              {(charts.expense_by_category || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip formatter={(v) => formatINRFull(v)} /></RPie>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Cash vs Bank</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RPie><Pie data={charts.cash_vs_bank || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
              <Cell fill="#1B5486" /><Cell fill="#D97706" />
            </Pie><Tooltip formatter={(v) => formatINRFull(v)} /></RPie>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Monthly Expense Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={charts.monthly_expenses || []}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/100000).toFixed(0)}L`} />
              <Tooltip formatter={(v) => formatINRFull(v)} />
              <Bar dataKey="value" fill="#DC2626" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

// Sales Dashboard
function SalesDashboard({ data }) {
  const { kpis, charts } = data;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard title="My Pipeline" value={formatINRFull(kpis.my_pipeline_value)} icon={TrendingUp} color="bg-[#1B5486]" sub={`${kpis.my_active_leads} active`} />
        <KPICard title="My Leads" value={kpis.my_leads_count} icon={Users} color="bg-slate-600" />
        <KPICard title="Won Value" value={formatINRFull(kpis.my_won_value)} icon={Target} color="bg-emerald-600" />
        <KPICard title="Conversion Rate" value={`${kpis.my_conversion_rate}%`} icon={BarChart3} color="bg-amber-600" />
        <KPICard title="Active Leads" value={kpis.my_active_leads} icon={FolderKanban} color="bg-purple-600" />
        <KPICard title="Quotations" value={kpis.my_quotations} icon={FileText} color="bg-orange-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">My Sales Funnel</h3>
          <div className="space-y-2">
            {Object.entries(charts.my_funnel || {}).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(status)} min-w-[100px] text-center`}>{formatStatus(status)}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                  <div className="bg-[#1B5486] h-full rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(count / Math.max(...Object.values(charts.my_funnel || {1:1})) * 100, 8)}%` }}>
                    <span className="text-[10px] text-white font-semibold">{count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Monthly Lead Activity</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={charts.monthly_leads || []}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#1B5486" radius={[4,4,0,0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

// Supervisor Dashboard
function SupervisorDashboard({ data }) {
  const { kpis, charts } = data;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <KPICard title="Assigned Projects" value={kpis.assigned_projects} icon={FolderKanban} color="bg-[#1B5486]" />
        <KPICard title="DWRs Submitted" value={kpis.submitted_dwrs} icon={FileText} color="bg-emerald-600" sub={`Draft: ${kpis.draft_dwrs} | Approved: ${kpis.approved_dwrs}`} />
        <KPICard title="Workers Today" value={kpis.workers_today} icon={Users} color="bg-amber-600" />
        <KPICard title="Incidents" value={kpis.incidents} icon={AlertTriangle} color="bg-red-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">My Projects</h3>
          {(charts.my_projects || []).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No projects assigned</p>
          ) : (
            <div className="space-y-2">
              {(charts.my_projects || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-800">{p.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(p.status)}`}>{formatStatus(p.status)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Recent DWRs</h3>
          {(charts.recent_dwrs || []).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No recent DWRs</p>
          ) : (
            <div className="space-y-2">
              {(charts.recent_dwrs || []).map((d, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-700">{d.date}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    d.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                    d.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>{d.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// HR Admin Dashboard
function HRDashboard({ data }) {
  const { kpis, charts } = data;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard title="Headcount" value={kpis.total_headcount} icon={Users} color="bg-[#1B5486]" sub={`${kpis.inactive_count} inactive`} />
        <KPICard title="Attendance Today" value={kpis.attendance_today} icon={ClipboardCheck} color="bg-emerald-600" />
        <KPICard title="Attendance %" value={`${kpis.attendance_percent}%`} icon={BarChart3} color="bg-amber-600" />
        <KPICard title="Pending Salary" value={kpis.pending_salary} icon={IndianRupee} color="bg-red-500" />
        <KPICard title="Advance Balance" value={formatINRFull(kpis.advance_balance)} icon={CreditCard} color="bg-purple-600" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Employees by Type</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RPie><Pie data={charts.emp_by_type || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#94a3b8' }}>
              {(charts.emp_by_type || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip /></RPie>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Employees by Department</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={charts.emp_by_department || []}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#1B5486" radius={[4,4,0,0]} name="Employees" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

const ROLE_TITLES = {
  DIRECTOR: 'Executive Overview',
  ACCOUNTANT: 'Financial Overview',
  SALES: 'My Sales Performance',
  SUPERVISOR: 'Site Operations',
  HR_ADMIN: 'HR Overview'
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/dashboard').then(res => {
      setData(res.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="text-center text-slate-500 py-12">Failed to load dashboard data</div>;

  const role = data.role || user?.role || 'DIRECTOR';

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">{ROLE_TITLES[role] || 'Overview'}</p>
      </div>
      {role === 'DIRECTOR' && <DirectorDashboard data={data} />}
      {role === 'ACCOUNTANT' && <AccountantDashboard data={data} />}
      {role === 'SALES' && <SalesDashboard data={data} />}
      {role === 'SUPERVISOR' && <SupervisorDashboard data={data} />}
      {role === 'HR_ADMIN' && <HRDashboard data={data} />}
    </div>
  );
}
